/**
 * Handoff from Jobs tab (or dashboard) → Agent optimize flow.
 * Written on "Optimize & Apply"; consumed once when Agent mounts with profile ready.
 */
import type { AgentJob } from '../../../ai-agent/components/AgentJobsPanel';
import type { Job } from '../JobCard';

export const AGENT_OPTIMIZE_HANDOFF_KEY = 'agent.pendingOptimizeJob';

export function jobToAgentJob(job: Job): AgentJob {
  return {
    id: job.id,
    title: job.title,
    company: job.company,
    company_logo_url: job.company_logo_url ?? null,
    location: job.location ?? null,
    remote: job.remote ?? null,
    job_type: job.job_type ?? null,
    experience_level: job.experience_level ?? null,
    skills: job.skills ?? null,
    must_have_skills: job.must_have_skills ?? job.skills ?? null,
    good_to_have_skills: job.good_to_have_skills ?? null,
    url: job.url ?? null,
    salary_min: job.salary_min ?? null,
    salary_max: job.salary_max ?? null,
    source_channel: job.source_channel ?? job.source ?? null,
    salary_currency: job.salary_currency ?? null,
    compensation_text: job.compensation_text ?? null,
    posted_at: job.posted_at ?? job.created ?? null,
    description_md: job.description ?? null,
  };
}

export function writeAgentOptimizeHandoff(job: Job): void {
  try {
    sessionStorage.setItem(
      AGENT_OPTIMIZE_HANDOFF_KEY,
      JSON.stringify({ job: jobToAgentJob(job), ts: Date.now() }),
    );
  } catch {
    // ignore quota errors
  }
}

function parseHandoffRaw(raw: string | null): AgentJob | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { job?: AgentJob };
    return parsed.job?.id ? parsed.job : null;
  } catch {
    return null;
  }
}

/** True while Jobs tab handoff is waiting (bootstrap must not restore job-search session). */
export function hasAgentOptimizeHandoff(): boolean {
  return parseHandoffRaw(sessionStorage.getItem(AGENT_OPTIMIZE_HANDOFF_KEY)) != null;
}

/** Read and clear the pending handoff (single consume). */
export function consumeAgentOptimizeHandoff(): AgentJob | null {
  try {
    const raw = sessionStorage.getItem(AGENT_OPTIMIZE_HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AGENT_OPTIMIZE_HANDOFF_KEY);
    return parseHandoffRaw(raw);
  } catch {
    sessionStorage.removeItem(AGENT_OPTIMIZE_HANDOFF_KEY);
    return null;
  }
}
